import { useSelector } from 'react-redux';
import { useEffect, useRef, useState } from 'react';
import { dateToUtc } from '../../../functions';
import { RootState } from '../../../redux/rootReducer';
import { getDateDaysFromNow } from '../../fuctions';
import { useUsersDopdown } from '../../hooks';
import { getLegs } from '../api';

export default function useLegsHistory(isAdmin: boolean) {
  const userData = useSelector(
    (state: RootState) => state.mainAppData.userData,
  );
  const initialState = {
    search: '',
    startDate: getDateDaysFromNow(-14),
    endDate: getDateDaysFromNow(1),
    userEmail: isAdmin ? '' : (userData?.username || ''),
    subAccountNumber: '',
    data: [],
    page: 1,
    pageSize: 25,
    count: 0,
    pages: 0,
    loading: false,
  };

  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [search, setSearch] = useState<string>(initialState.search);
  const [startDate, setStartDate] = useState<Date | null>(initialState.startDate);
  const [endDate, setEndDate] = useState<Date | null>(initialState.endDate);
  const [userEmail, setUserEmail] = useState<string>(initialState.userEmail);
  const [subAccountNumber, setSubAccountNumber] = useState<string>(initialState.subAccountNumber);
  const [data, setData] = useState(initialState.data);
  const [page, setPage] = useState<number>(initialState.page);
  const [pageSize, setPageSize] = useState<number>(initialState.pageSize);
  const [count, setCount] = useState<number>(initialState.count);
  const [pages, setPages] = useState<number>(initialState.pages);
  const [loading, setLoading] = useState<boolean>(initialState.loading);
  const {
    options: usersOptions,
    loading: loadingUsersOptions,
  } = useUsersDopdown(isAdmin);
  
  const curPayload = {
    search,
    startDate,
    endDate,
    userEmail,
    subAccountNumber,
    page,
    pageSize,
  };

  const fetchLegs = async (payload: any) => {
    const {
      legs,
      legsCount,
      legsPages,
    } = await getLegs(
      payload,
      setLoading,
      isAdmin,
    );
    setData(legs);
    setCount(legsCount);
    setPages(legsPages);
  };

  const onChangeSearch = (newSearch: string) => {
    setSearch(newSearch);
    if (searchTimeoutRef?.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(() => {
      setPage(initialState.page);
      fetchLegs({
        ...curPayload,
        search: newSearch,
        page: initialState.page,
      });
    }, 500);
  };

  const onChangeDateRange = (dates: [Date | null, Date | null]) => {
    const [start, end] = dates;
    setStartDate(start);
    setEndDate(end);
    setPage(initialState.page);
    if (start && end) {
      fetchLegs({
        ...curPayload,
        startDate: start,
        endDate: end,
        page: initialState.page,
      });
    }
  };

  const onChangeDateRangeRaw = (event: React.FocusEvent<HTMLInputElement>) => {
    const input = event.target.value;
    if (
      !input ||
      !input.match(/\d{4}-\d{2}-\d{2}\s-\s\d{4}-\d{2}-\d{2}/)
    ) {
      return;
    }
    const [start, end] = input.split(' - ').map((date: string) => {
      const parsedDate = dateToUtc(new Date(date.trim()));
      if (!parsedDate?.getTime()) {
        return null;
      }
      return parsedDate;
    })
    if(start && end) {
      setStartDate(start);
      setEndDate(end);
      setPage(initialState.page);
      fetchLegs({
        ...curPayload,
        startDate: start,
        endDate: end,
        page: initialState.page,
      })
    }
  };

  const onChangeUserEmail = (newEmail: string) => {
    setUserEmail(newEmail);
    setPage(initialState.page);
    fetchLegs({
      ...curPayload,
      userEmail: newEmail,
      page: initialState.page,
    });
  };

  const onChangeSubAccountNumber = (newSubAccountNumber: string) => {
    setSubAccountNumber(newSubAccountNumber);
    setPage(initialState.page);
    fetchLegs({
      ...curPayload,
      subAccountNumber: newSubAccountNumber,
      page: initialState.page,
    });
  };

  const onChangePage = (newPage: number) => {
    setPage(newPage);
    fetchLegs({
      ...curPayload,
      page: newPage,
    });
  };

  const onChangePageSize = (newPageSize: number) => {
    setPage(initialState.page);
    setPageSize(newPageSize);
    fetchLegs({
      ...curPayload,
      page: initialState.page,
      pageSize: newPageSize,
    });
  };

  const resetState = () => {
    setSearch(initialState.search);
    setStartDate(initialState.startDate);
    setEndDate(initialState.endDate);
    setUserEmail(initialState.userEmail);
    setSubAccountNumber(initialState.subAccountNumber);
    setData(initialState.data);
    setPage(initialState.page);
    setPageSize(initialState.pageSize);
    setCount(initialState.count);
    setPages(initialState.pages);
    setLoading(initialState.loading);
  };

  useEffect(() => {
    resetState();
    fetchLegs({
      search: initialState.search,
      startDate: initialState.startDate,
      endDate: initialState.endDate,
      userEmail: initialState.userEmail,
      subAccountNumber: initialState.subAccountNumber,
      page: initialState.page,
      pageSize: initialState.pageSize,
    });
  }, [isAdmin]);

  useEffect(() => {
    return () => {
      if (searchTimeoutRef?.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  return {
    search,
    onChangeSearch,
    startDate,
    endDate,
    onChangeDateRange,
    onChangeDateRangeRaw,
    userEmail,
    onChangeUserEmail,
    usersOptions,
    subAccountNumber,
    onChangeSubAccountNumber,
    data,
    page,
    onChangePage,
    pageSize,
    onChangePageSize,
    count,
    pages,
    loading: loading || loadingUsersOptions,
  };
}
